// WordPress REST API Service
// This service handles communication with WordPress via REST API

/**
 * WP REST API oczekuje daty w formacie "YYYY-MM-DDTHH:MM:SS" — BEZ trailing Z,
 * BEZ "+00:00" offsetu, BEZ milisekund. JS toISOString() zwraca z .000Z na końcu,
 * a niektóre źródła DB serializują z "+00:00". Strip wszystkiego po sekundach.
 *
 * Per WP REST docs (/wp/v2/posts):
 *   date     — "in the site's timezone"
 *   date_gmt — "as GMT"
 * Format sample: "2023-10-27T10:00:00"
 */
function stripTimezoneMarker(iso: string): string {
  const match = iso.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/);
  return match ? match[1] : iso;
}

export interface WordPressCredentials {
  url: string;
  username: string;
  password: string;
}

export interface WordPressPost {
  title: string;
  content: string;
  status?: 'draft' | 'publish' | 'private' | 'pending';
  /** Array of WP category IDs (numeric). NOTE: changed from string[] to number[] zeby pasowac do WP REST. */
  categories?: number[];
  tags?: number[];
  featured_media?: number;
  slug?: string;
  excerpt?: string;
  /**
   * Custom publish date — backdated history support (per WP REST docs /wp/v2/posts).
   * Format: ISO 8601 BEZ timezone marker (np. "2026-05-04T10:30:00"). WP REST sam
   * interpretuje wg site timezone (date) lub jako GMT (date_gmt).
   * date_gmt ma priorytet — jeśli set, WP używa tego do post_date_gmt + autoderyvuje
   * post_date wg site offset.
   * NULL/undefined → WP defaultuje do NOW (klasyczny publish behaviour).
   */
  date?: string;
  date_gmt?: string;
  /**
   * WP post meta (REST). Używane do Yoast SEO meta fields:
   *   _yoast_wpseo_title       — Yoast SEO title (osobne od post.title; idzie do <title> + og:title)
   *   _yoast_wpseo_metadesc    — Yoast meta description (idzie do <meta name="description">)
   *
   * WAŻNE: Yoast >= 14 obsługuje te meta przez WP REST automatycznie (show_in_rest=true).
   * Yoast Free + Premium oba działają. Aby było skuteczne, user WP musi mieć cap edit_post.
   */
  meta?: Record<string, string>;
}

export interface WordPressCategory {
  id: number;
  name: string;
  slug: string;
  parent: number;
  description?: string;
  count?: number;
}

export interface WordPressMedia {
  id: number;
  url: string;
  title: string;
  alt?: string;
}

export interface WordPressResponse {
  success: boolean;
  data?: any;
  error?: string;
}

class WordPressService {
  private credentials: WordPressCredentials | null = null;

  setCredentials(credentials: WordPressCredentials) {
    this.credentials = credentials;
  }

  /** Zwraca snapshot aktualnych credentials (lub null). Uzywane przy temp-override
   *  z restore patternem zeby nie zostawic state'u dla nastepnych callerow. */
  getCredentials(): WordPressCredentials | null {
    return this.credentials ? { ...this.credentials } : null;
  }

  private getAuthHeaders(): Record<string, string> {
    if (!this.credentials) {
      throw new Error('WordPress credentials not set');
    }

    // Create Basic Auth header (same as Python version)
    const credentials = `${this.credentials.username}:${this.credentials.password}`;
    const encodedCredentials = btoa(credentials);

    return {
      'Authorization': `Basic ${encodedCredentials}`,
      'Content-Type': 'application/json',
      'User-Agent': 'WordPressPublishing/1.0'
    };
  }

  private async makeRESTRequest(endpoint: string, options: RequestInit = {}): Promise<WordPressResponse> {
    if (!this.credentials) {
      return { success: false, error: 'WordPress credentials not set' };
    }

    try {
      // Direct connection to WordPress REST API (like in Python version)
      const cleanUrl = this.credentials.url.replace(/\/$/, ''); // Remove trailing slash
      const url = `${cleanUrl}/wp-json/wp/v2${endpoint}`;

      console.log('WordPress REST API URL:', url);
      console.log('Direct connection to:', cleanUrl);

      const headers = this.getAuthHeaders();

      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('WordPress API Error:', response.status, errorText);
        console.error('Request URL:', url);
        return {
          success: false,
          error: `HTTP ${response.status}: ${errorText || response.statusText}`
        };
      }

      const data = await response.json();

      return {
        success: true,
        data
      };

    } catch (error) {
      console.error('REST Request failed:', error);
      console.error('Target URL:', this.credentials.url);
      console.error('Endpoint:', endpoint);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  private createXMLRPCRequest(method: string, params: any[]): string {
    if (!this.credentials) {
      throw new Error('WordPress credentials not set');
    }

    // Add authentication parameters
    const authParams = [this.credentials.username, this.credentials.password];
    const allParams = [...authParams, ...params];

    let xml = '<?xml version="1.0"?>';
    xml += '<methodCall>';
    xml += `<methodName>${method}</methodName>`;
    xml += '<params>';

    allParams.forEach(param => {
      xml += '<param>';
      xml += this.serializeParam(param);
      xml += '</param>';
    });

    xml += '</params>';
    xml += '</methodCall>';

    return xml;
  }

  private serializeParam(value: any): string {
    if (typeof value === 'string') {
      return `<value><string>${this.escapeXml(value)}</string></value>`;
    }

    if (typeof value === 'number') {
      return `<value><int>${value}</int></value>`;
    }

    if (typeof value === 'boolean') {
      return `<value><boolean>${value ? 1 : 0}</boolean></value>`;
    }

    if (Array.isArray(value)) {
      let xml = '<value><array><data>';
      value.forEach(item => {
        xml += '<value>';
        xml += this.serializeParam(item);
        xml += '</value>';
      });
      xml += '</data></array></value>';
      return xml;
    }

    if (typeof value === 'object' && value !== null) {
      let xml = '<value><struct>';
      Object.entries(value).forEach(([key, val]) => {
        xml += '<member>';
        xml += `<name>${this.escapeXml(key)}</name>`;
        xml += this.serializeParam(val);
        xml += '</member>';
      });
      xml += '</struct></value>';
      return xml;
    }

    return `<value><string>${this.escapeXml(String(value))}</string></value>`;
  }

  private parseXMLRPCResponse(xml: string): any {
    // Simple XML-RPC response parser
    // In a real implementation, you'd use a proper XML-RPC library
    try {
      // For now, return a mock successful response
      // This should be replaced with proper XML parsing
      return { success: true };
    } catch (error) {
      console.error('Failed to parse XML-RPC response:', error);
      return null;
    }
  }

  private escapeXml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  // Public methods

  // Utility function to convert Polish characters to ASCII
  private convertToAscii(text: string): string {
    const polishChars: { [key: string]: string } = {
      'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z',
      'Ą': 'A', 'Ć': 'C', 'Ę': 'E', 'Ł': 'L', 'Ń': 'N', 'Ó': 'O', 'Ś': 'S', 'Ź': 'Z', 'Ż': 'Z'
    };

    return text.replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, (match) => polishChars[match] || match);
  }

  async uploadMedia(file: File, filename: string, altText?: string): Promise<WordPressResponse> {
    try {
      // Create FormData for file upload (same as Python version)
      const formData = new FormData();
      formData.append('file', file, filename);

      // Convert filename to ASCII and create clean title
      const cleanFilename = this.convertToAscii(filename.replace('.webp', ''));
      const cleanTitle = cleanFilename.replace(/-/g, ' ');
      const cleanAltText = altText ? this.convertToAscii(altText) : cleanTitle;

      formData.append('title', cleanTitle); // Add title for WordPress
      formData.append('post_title', cleanTitle); // Additional title parameter
      formData.append('filename', filename); // Explicit filename parameter
      formData.append('alt_text', cleanAltText); // Add alt text without "AI generated image"

      // Get auth headers but don't set Content-Type for FormData
      const authHeaders = this.getAuthHeaders();
      const headers = { ...authHeaders };
      // Don't set Content-Type for FormData - let browser set it automatically

      // For FormData, we need to handle the request differently
      // because makeRESTRequest merges headers which causes Content-Type issues
      const cleanUrl = this.credentials.url.replace(/\/$/, '');
      const url = `${cleanUrl}/wp-json/wp/v2/media`;

      console.log('WordPress REST API URL:', url);
      console.log('Direct connection to:', cleanUrl);

      console.log('WordPress upload request:', {
        url,
        method: 'POST',
        username: this.credentials.username,
        hasPassword: !!this.credentials.password,
        fileName: filename,
        fileSize: file.size,
        fileType: file.type
      });

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Basic ${btoa(`${this.credentials.username}:${this.credentials.password}`)}`,
          // Don't set Content-Type for FormData - let browser set it automatically
        }
      });

      console.log('WordPress upload response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      // WordPress returns 201 Created for successful media uploads
      if (response.status !== 201 && response.status !== 200 && !response.ok) {
        const errorText = await response.text();
        console.error('WordPress API Error:', response.status, errorText);
        console.error('Request URL:', url);
        console.error('Request headers:', {
          'Authorization': 'Basic ***',
          'Content-Type': 'auto (FormData)'
        });
        return {
          success: false,
          error: `HTTP ${response.status}: ${errorText || response.statusText}`
        };
      }

      const data = await response.json();

      // Check if we have valid response data from WordPress
      if (data && data.id) {
        return {
          success: true,
          data: {
            id: data.id,
            url: data.source_url || data.url,
            title: data.title?.rendered || cleanTitle,
            alt: data.alt_text || cleanAltText
          }
        };
      }

      return {
        success: false,
        error: 'Unexpected response format from WordPress'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload media'
      };
    }
  }

  async updateMediaAltText(mediaId: number, altText: string): Promise<WordPressResponse> {
    try {
      // Nie konwertuj polskich znaków w alt text - zachowaj je!
      const cleanAltText = altText;

      console.log(`🔄 Updating alt text for media ${mediaId} to: "${cleanAltText}"`);
      console.log(`Full request URL: ${this.credentials?.url}/wp-json/wp/v2/media/${mediaId}`);

      // Try different parameter names that WordPress might expect
      const updateData = {
        alt_text: cleanAltText,
        // Also try these alternative parameter names
        altText: cleanAltText,
        'alt-text': cleanAltText
      };

      // Also try with just the essential parameter
      const simpleUpdateData = {
        alt_text: cleanAltText
      };

      console.log('Trying with full data:', updateData);
      console.log('Will also try with simple data:', simpleUpdateData);

      console.log('Sending update data:', updateData);

      // Try both POST and PUT methods as WordPress might use either
      let response = await this.makeRESTRequest(`/media/${mediaId}`, {
        method: 'POST',
        body: JSON.stringify(updateData)
      });

      // If POST fails, try PUT method
      if (!response.success) {
        console.log('POST method failed, trying PUT method...');
        response = await this.makeRESTRequest(`/media/${mediaId}`, {
          method: 'PUT',
          body: JSON.stringify(updateData)
        });
      }

      // If both fail, try PATCH method
      if (!response.success) {
        console.log('PUT method failed, trying PATCH method...');
        response = await this.makeRESTRequest(`/media/${mediaId}`, {
          method: 'PATCH',
          body: JSON.stringify(updateData)
        });
      }

      // If PATCH also fails, try with simple data using POST
      if (!response.success) {
        console.log('PATCH method failed, trying POST with simple data...');
        response = await this.makeRESTRequest(`/media/${mediaId}`, {
          method: 'POST',
          body: JSON.stringify(simpleUpdateData)
        });
      }

      console.log('Update response:', {
        success: response.success,
        status: response.data?.id ? 'Data received' : 'No data',
        error: response.error,
        fullResponse: response
      });

      if (response.success && response.data) {
        console.log(`✅ Successfully updated alt text for media ${mediaId}`);
        console.log('Updated media data:', response.data);
        return {
          success: true,
          data: {
            id: response.data.id,
            alt_text: response.data.alt_text
          }
        };
      }

      console.error(`❌ Failed to update alt text for media ${mediaId}:`, {
        error: response.error,
        fullResponse: response,
        requestDetails: {
          url: `${this.credentials?.url}/wp-json/wp/v2/media/${mediaId}`,
          method: 'POST/PUT/PATCH',
          data: updateData
        }
      });

      // Log additional details if available
      if (response.data && typeof response.data === 'object') {
        console.log('WordPress error details:', response.data);
      }

      return response;
    } catch (error) {
      console.error(`💥 Error updating alt text for media ${mediaId}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update media alt text'
      };
    }
  }

  // =============================================================================
  // CATEGORIES (GET / CREATE) — uzywane przez seedCategoryMappingService
  // =============================================================================

  /**
   * GET /wp/v2/categories — lista wszystkich kategorii (paginated 100 per page).
   * Auto-iteruje strony az do koncow. Default WP zwraca max 100 per call.
   */
  async listCategories(): Promise<WordPressCategory[]> {
    const all: WordPressCategory[] = [];
    let page = 1;
    const perPage = 100;
    const maxPages = 20; // 2000 categories ceiling
    while (page <= maxPages) {
      const res = await this.makeRESTRequest(`/categories?per_page=${perPage}&page=${page}`);
      if (!res.success || !Array.isArray(res.data)) break;
      const batch: WordPressCategory[] = (res.data as any[]).map((c) => ({
        id: c.id,
        name: c.name,
        slug: c.slug,
        parent: c.parent ?? 0,
        description: c.description,
        count: c.count,
      }));
      all.push(...batch);
      if (batch.length < perPage) break;
      page++;
    }
    return all;
  }

  /**
   * GET /wp/v2/categories?slug=X — znajdz kategorie po slugu.
   * Zwraca pierwszy match albo null.
   * Uzywane przed createCategory zeby uniknac duplikatu (REUSE existing).
   */
  async findCategoryBySlug(slug: string): Promise<WordPressCategory | null> {
    const res = await this.makeRESTRequest(`/categories?slug=${encodeURIComponent(slug)}`);
    if (!res.success || !Array.isArray(res.data) || res.data.length === 0) return null;
    const c = res.data[0] as any;
    return {
      id: c.id,
      name: c.name,
      slug: c.slug,
      parent: c.parent ?? 0,
      description: c.description,
      count: c.count,
    };
  }

  /**
   * POST /wp/v2/categories — utworz nowa kategorie.
   * Wymaga uprawnien `manage_categories` (Editor lub Admin role).
   * Jesli slug juz zajety w WP → 400 term_exists; uzyj findCategoryBySlug PRZED.
   */
  async createCategory(data: {
    name: string;
    slug?: string;
    description?: string;
    parent?: number;
  }): Promise<{ success: boolean; data?: WordPressCategory; error?: string }> {
    const body: any = { name: data.name };
    if (data.slug) body.slug = data.slug;
    if (data.description) body.description = data.description;
    if (data.parent && data.parent > 0) body.parent = data.parent;

    const res = await this.makeRESTRequest("/categories", {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (!res.success || !res.data) {
      return { success: false, error: res.error || "createCategory failed" };
    }
    const c = res.data as any;
    return {
      success: true,
      data: {
        id: c.id,
        name: c.name,
        slug: c.slug,
        parent: c.parent ?? 0,
        description: c.description,
        count: c.count,
      },
    };
  }

  // =============================================================================
  // POSTS (CREATE / UPDATE)
  // =============================================================================

  async createPost(post: WordPressPost): Promise<WordPressResponse> {
    try {
      const postData: any = {
        title: post.title,
        content: post.content,
        status: post.status || 'draft'
      };

      // Add featured media if provided
      if (post.featured_media) {
        postData.featured_media = post.featured_media;
      }
      // Add categories (array of int IDs) — WP REST oczekuje array of numeric IDs
      if (post.categories && post.categories.length > 0) {
        postData.categories = post.categories;
      }
      if (post.tags && post.tags.length > 0) {
        postData.tags = post.tags;
      }
      if (post.slug) {
        postData.slug = post.slug;
      }
      if (post.excerpt) {
        postData.excerpt = post.excerpt;
      }
      // Yoast SEO meta fields (REST API). Yoast 14+ obsługuje show_in_rest.
      if (post.meta && Object.keys(post.meta).length > 0) {
        postData.meta = post.meta;
      }
      // Backdated publish date — strip timezone marker (WP REST oczekuje "YYYY-MM-DDTHH:MM:SS" bez Z/+00:00).
      // date_gmt ma priorytet w WP — gdy set, post_date jest autoderyvowane wg site timezone offset.
      if (post.date_gmt) {
        postData.date_gmt = stripTimezoneMarker(post.date_gmt);
      }
      if (post.date) {
        postData.date = stripTimezoneMarker(post.date);
      }

      const response = await this.makeRESTRequest('/posts', {
        method: 'POST',
        body: JSON.stringify(postData)
      });

      if (response.success && response.data) {
        return {
          success: true,
          data: {
            postId: response.data.id,
            url: response.data.link,
            featured_media: response.data.featured_media
          }
        };
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create post'
      };
    }
  }

  /**
   * Tworzy STRONĘ WP (post_type=page) — endpoint /pages. Mirror createPost, minimalny payload.
   * Używane przez feature "Unikalne podstrony" + publikację strony autora. Nie rusza createPost.
   */
  async createPage(page: { title: string; content: string; status?: string; slug?: string }): Promise<WordPressResponse> {
    try {
      const pageData: any = {
        title: page.title,
        content: page.content,
        status: page.status || 'draft',
      };
      if (page.slug) pageData.slug = page.slug;

      const response = await this.makeRESTRequest('/pages', {
        method: 'POST',
        body: JSON.stringify(pageData),
      });

      if (response.success && response.data) {
        return {
          success: true,
          data: { postId: response.data.id, url: response.data.link },
        };
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create page',
      };
    }
  }

  /**
   * Usuwa wpis. force=true → permanent delete (omija kosz) — używane przez
   * test połączenia żeby nie zostawiać śmieci-szkiców na domenie.
   */
  async deletePost(wpPostId: number, force = true): Promise<WordPressResponse> {
    try {
      const response = await this.makeRESTRequest(
        `/posts/${wpPostId}?force=${force ? 'true' : 'false'}`,
        { method: 'DELETE' },
      );
      if (response.success) {
        return { success: true, data: { deleted: true, postId: wpPostId } };
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete post',
      };
    }
  }

  /**
   * Refurbish flow: update istniejacego wpisu WP.
   * WP REST uzywa POST /posts/{id} dla update (nie PUT).
   */
  async updatePost(
    wpPostId: number,
    data: {
      title?: string;
      content?: string;
      excerpt?: string;
      status?: 'publish' | 'draft' | 'private' | 'pending';
      featured_media?: number;
      slug?: string;
      categories?: number[];
      tags?: number[];
      meta?: Record<string, unknown>;
    }
  ): Promise<WordPressResponse> {
    try {
      const response = await this.makeRESTRequest(`/posts/${wpPostId}`, {
        method: 'POST',
        body: JSON.stringify(data),
      });

      if (response.success && response.data) {
        return {
          success: true,
          data: {
            postId: response.data.id,
            url: response.data.link,
            modified: response.data.modified,
            status: response.data.status,
            featured_media: response.data.featured_media,
            raw: response.data,
          },
        };
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update post',
      };
    }
  }

  /**
   * M15 — Soft delete: ustawia status=draft w WP (wpis znika z listy publish
   * ale pozostaje w bazie WP). Rollback-able przez restorePost.
   */
  async softDeletePost(wpPostId: number): Promise<WordPressResponse> {
    return this.updatePost(wpPostId, { status: 'draft' });
  }

  /**
   * M15 — Restore z trash: przywraca status (default 'publish').
   */
  async restorePost(
    wpPostId: number,
    statusBefore: 'publish' | 'draft' | 'private' | 'pending' = 'publish',
  ): Promise<WordPressResponse> {
    return this.updatePost(wpPostId, { status: statusBefore });
  }

  /**
   * Refurbish flow: pobierz pojedynczy wpis z pelnymi polami raw (title.raw, content.raw)
   * — potrzebne do backupu przed UPDATE.
   * context=edit wymaga authenticated user z edit_posts capability.
   */
  async fetchPost(wpPostId: number): Promise<WordPressResponse> {
    try {
      const response = await this.makeRESTRequest(`/posts/${wpPostId}?context=edit`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch post',
      };
    }
  }

  /**
   * Znajdź wp_post_id po slug (backfill scenarios — gdy DB ma URL ale nie ID).
   * Idzie przez nginx proxy = same-origin.
   */
  async findPostIdBySlug(slug: string): Promise<number | null> {
    try {
      const response = await this.makeRESTRequest(`/posts?slug=${encodeURIComponent(slug)}&_fields=id&status=any`);
      if (response.success && Array.isArray(response.data) && response.data.length > 0) {
        return response.data[0].id || null;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Audit flow: pobierz LIVE rendered HTML jakiego widzi czytelnik (z schema-ldjson injection,
   * Yoast meta tags, etc.). Context=view zwraca rendered (NIE raw). Idzie przez nginx proxy
   * w same-origin (zero CORS).
   */
  async fetchPostForAudit(wpPostId: number): Promise<WordPressResponse> {
    try {
      const response = await this.makeRESTRequest(`/posts/${wpPostId}?context=view&_fields=id,date,date_gmt,modified,modified_gmt,link,title,content,meta`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch post for audit',
      };
    }
  }

  async getPosts(options: { number?: number; offset?: number } = {}): Promise<WordPressResponse> {
    try {
      const params = new URLSearchParams({
        per_page: (options.number || 10).toString(),
        offset: (options.offset || 0).toString(),
        status: 'any'
      });

      const response = await this.makeRESTRequest(`/posts?${params}`);

      if (response.success && Array.isArray(response.data)) {
        return {
          success: true,
          data: response.data.map(post => ({
            id: post.id,
            title: post.title?.rendered || '',
            content: post.content?.rendered || '',
            status: post.status,
            date: post.date,
            url: post.link
          }))
        };
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get posts'
      };
    }
  }

  async testConnection(): Promise<WordPressResponse> {
    try {
      // Test connection by trying to get current user info
      const response = await this.makeRESTRequest('/users/me');

      if (response.success && response.data) {
        return {
          success: true,
          data: {
            user: response.data,
            connected: true
          }
        };
      }

      return {
        success: false,
        error: 'Could not connect to WordPress or invalid credentials'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Connection test failed'
      };
    }
  }

  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}

// Export singleton instance
export const wordpressService = new WordPressService();
export default wordpressService;
